import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import Label from "./Label";
import { District } from "src/types";
import { colors } from "src/styles/theme";

export function usePopupLocation(args?: { defaultSelectIds?: number[]; items?: any }) {
  // TODO: useModal hook 으로 리팩토링
  const [isPopupShown, setIsPopupShown] = useState(false);
  const [activeParentId, setActiveParentId] = useState<number | null>(null);
  const [activeChildId, setActiveChildId] = useState<number | null>(null);
  const [activeDistrictObj, setActiveDistrictObj] = useState<{ id: number; name: string }>();

  const propsToInject = {
    activeParentId,
    activeChildId,
    setActiveParentId,
    setActiveChildId,
    isPopupShown,
    setIsPopupShown,
    activeDistrictObj,
    setActiveDistrictObj,
    defaultSelectIds: args?.defaultSelectIds,
  };

  useEffect(() => {
    const defaultId = args?.defaultSelectIds?.[0];
    if (!defaultId) {
      return;
    }
    const defaultParentId = String(defaultId).slice(0, 2);
    const defaultChildId = String(defaultId).slice(2, 5);
    if (Number(defaultParentId)) {
      setActiveParentId(Number(defaultParentId));
    }
    if (Number(defaultChildId)) {
      setActiveChildId(Number(defaultChildId));
    }
  }, [args?.defaultSelectIds?.[0]]);

  const PopupLocation = useCallback(
    (props: {
      tabIndex?: number;
      inputId?: string;
      items?: District[];
      defaultSelectIds?: number[];
      placeholder?: string;
      label?: string;
      boldLabel?: boolean;
      isEssential?: boolean;
      disabled?: boolean;
      activeParentId: number | null;
      activeChildId: number | null;
      setActiveChildId: React.Dispatch<React.SetStateAction<number | null>>;
      setActiveParentId: React.Dispatch<React.SetStateAction<number | null>>;
      isPopupShown: boolean;
      setIsPopupShown: React.Dispatch<React.SetStateAction<boolean>>;
      activeDistrictObj:
        | {
            id: number;
            name: string;
          }
        | undefined;
      setActiveDistrictObj: React.Dispatch<
        React.SetStateAction<
          | {
              id: number;
              name: string;
            }
          | undefined
        >
      >;
    }) => {
      const handleConfirm = () => {
        props.setActiveDistrictObj(() => {
          return {
            id: Number(String(props.activeParentId).padStart(5, "0")),
            name: (() => {
              const activeParent = props.items?.find((i) => i.cd === props.activeParentId);
              return activeParent?.name ?? "";
            })(),
          };
        });
        props.setIsPopupShown(false);
      };

      const handleCancel = () => {
        props.setIsPopupShown(false);
      };

      return (
        <>
          <Container
            className={props.activeParentId ? "active" : ""}
            onClick={() => !props.disabled && props.setIsPopupShown(true)}
            $disabled={props.disabled}
          >
            {props.label && (
              <Label
                content={props.label}
                isEssential={props.isEssential}
                className={`${props.boldLabel ? "bold" : ""} mb-1`}
              />
            )}
            <div
              className="content-main input-box-type d-flex jc-sb ai-c"
              tabIndex={props.tabIndex}
              id={props.inputId || `${Date.now()}-group-lc`}
            >
              {props.activeParentId ? (
                (() => {
                  const p = props.items?.find((v) => {
                    return v.cd === props.activeParentId;
                  });
                  return p?.name || "";
                })()
              ) : (
                <div className="placeholder">{props.placeholder}</div>
              )}
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7 10l5 5 5-5"
                  stroke="#79818a"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </Container>
          {props.isPopupShown && (
            <ModalOverlay onClick={handleCancel}>
              <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalContent>
                  <ModalHeader>
                    <ModalTitle>활동 지역</ModalTitle>
                  </ModalHeader>

                  <OptionsContainer>
                    {props.items?.map((parent) => (
                      <OptionButton
                        key={parent.cd}
                        isSelected={props.activeParentId === parent.cd}
                        onClick={() => {
                          props.setActiveParentId((currentId) =>
                            currentId === parent.cd ? null : parent.cd
                          );
                        }}
                      >
                        <OptionContent>
                          <CheckIcon isActive={props.activeParentId === parent.cd}>
                            <svg
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M8.6 14.6L15.65 7.55L14.25 6.15L8.6 11.8L5.75 8.95L4.35 10.35L8.6 14.6ZM10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20Z"
                                fill="currentColor"
                              />
                            </svg>
                          </CheckIcon>
                          <OptionText>{parent.name}</OptionText>
                        </OptionContent>
                      </OptionButton>
                    ))}
                  </OptionsContainer>
                </ModalContent>

                <ModalButtons>
                  <CancelButton onClick={handleCancel}>뒤로</CancelButton>
                  <ConfirmButton onClick={handleConfirm}>확인</ConfirmButton>
                </ModalButtons>
              </ModalContainer>
            </ModalOverlay>
          )}
        </>
      );
    },
    [isPopupShown]
  );

  return { PopupLocation, propsToInject, activeChildId, activeParentId };
}

const Container = styled.div<{ $disabled?: boolean }>`
  label {
    display: inline-block;
  }
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};
  .content-main {
    .placeholder {
      color: ${colors.gray.first};
      font-size: 14px;
    }
  }
  &.active .content-main.input-box-type {
    border-color: ${colors.blue.third};
  }
  .content-main.input-box-type {
    background: ${(props) => (props.$disabled ? "#f0f0f4" : "#fdfdfd")};
    border-color: ${(props) => (props.$disabled ? "#d1d5d8" : "#d1d5d8")};
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(2, 2, 2, 0.86);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
  animation: fade-in 0.2s;

  @keyframes fade-in {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  max-width: 800px;
  width: calc(100% - 32px);
  height: auto;
  min-height: 400px;
  display: flex;
  flex-direction: column;
  border-radius: 6px;
  background: #fdfdfd;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.25);
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0;
`;

const ModalContent = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 24px;
  flex: 1;
`;

const ModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ModalTitle = styled.h2`
  align-self: stretch;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const OptionButton = styled.button<{ isSelected: boolean }>`
  display: flex;
  min-width: 120px;
  flex: 1;
  height: 40px;
  justify-content: flex-start;
  align-items: center;
  border-radius: 6px;
  background: #f8f9fa;
  border: 1px solid ${(props) => (props.isSelected ? "#007BFF" : "#E5E7EB")};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f1f3f4;
  }
`;

const OptionContent = styled.div`
  display: flex;
  padding: 8px 10px;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const CheckIcon = styled.div<{ isActive: boolean }>`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  aspect-ratio: 1/1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${(props) => (props.isActive ? "#007BFF" : "#D1D5D8")};
  color: white;
`;

const OptionText = styled.span`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  margin-left: 10px;
`;

const ModalButtons = styled.div`
  display: flex;
  height: 60px;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  width: 100%;
`;

const CancelButton = styled.button`
  display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 0 6px;
  background: #9ca3af;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
`;

const ConfirmButton = styled.button`
  display: flex;
  flex: 1;
  height: 60px;
  padding: 16px 46px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  border-radius: 0 0 6px 0;
  background: #004b49;
  border: none;
  cursor: pointer;
  color: #ffffff;
  text-align: center;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px;

  &:hover {
    opacity: 0.9;
  }
`;
